import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from 'primereact/dialog';
import { Button } from '../../../components/ui/Button';
import { InputText } from 'primereact/inputtext';
import { Checkbox } from 'primereact/checkbox';
import { RadioButton } from 'primereact/radiobutton';
import { Skeleton } from 'primereact/skeleton';
import { QRCodeSVG } from 'qrcode.react';

import Layout from '../../Layout/Layout';
import YouTubeSearchDialog from '../../Links/YouTubeSearchDialog';
import { useGetAllCompanyDevices } from '../../../queries/devices';
import {
  useGetBirthdaySessions,
  useGetBirthdayAudioSettings,
  useStartBirthdaySession,
  usePrepareBirthdaySession,
  useStartPreparedBirthdaySession,
  useEndBirthdaySession,
  useGetBirthdaySessionPhotos,
  useUpdateBirthdayAudioSettings,
  useGetAudioLibrary,
} from '../../../queries/birthday';
import { useCustomToast } from '../../Contexts/ToastContext';
import FeatureLocked from '../../SharedComponents/FeatureLocked';
import { useInHub } from '../../../nav/useInHub';

/**
 * Birthday Mode — flagship feature.
 *
 * Page structure:
 *   1. Hero with "Start Birthday" CTA (disabled while a session is running).
 *   2. Active-session card (visible only when status === 'ACTIVE')
 *      — countdown + QR code + photo grid + End button.
 *   3. Audio Templates Configuration card — 4 slots × (Library | YouTube) source.
 *   4. Past Celebrations table.
 *
 *   Start dialog: guest name + 4 fixed template radios with ▶ preview +
 *   "Search YouTube instead" one-off + screens + duration.
 *
 * Backend contract notes (see .claude/MEMORY.md for the canonical list):
 *   - POST /sessions/create body: birthday_person_name, template (enum),
 *     device_ids[], duration_minutes [, message_from, audio_url, audio_name].
 *   - GET /audio-settings: { template_arabic_url, template_english_url,
 *     template_calm_url, template_party_url } — flat URLs.
 *   - Active session detected by status === 'ACTIVE'.
 *   - QR URL is rendered as a string returned by the API as `qr_url`; the
 *     page does not construct it.
 */

const DURATION_PRESETS = [5, 10, 15, 30];   // minutes

const TEMPLATE_OPTIONS = [
  { id: 'arabic',  i18nKey: 'birthday.template_arabic',  urlKey: 'template_arabic_url',  emoji: '🇸🇦' },
  { id: 'english', i18nKey: 'birthday.template_english', urlKey: 'template_english_url', emoji: '🇬🇧' },
  { id: 'calm',    i18nKey: 'birthday.template_calm',    urlKey: 'template_calm_url',    emoji: '🧘' },
  { id: 'party',   i18nKey: 'birthday.template_party',   urlKey: 'template_party_url',   emoji: '🎉' },
];

// Quick-pick message presets. `id` is used as the radio value; `text` is what
// actually goes on the wire. Order matters — first item is the default.
const MESSAGE_PRESETS = [
  { id: 'happy',     emoji: '🎂', text: 'عيد ميلاد سعيد! / Happy Birthday!'   },
  { id: 'returns',   emoji: '🎉', text: 'كل عام وأنت بخير / Many Happy Returns' },
  { id: 'wishes',    emoji: '🌟', text: 'أطيب التمنيات / Best Wishes'           },
  { id: 'custom',    emoji: '✨', text: ''                                      }, // user-typed
];

// 5 premium visual themes (2026-06-06) — must match backend
// ENUM('party_neon','royal_gold','wow_pop','royal_dark','sweet_candy').
// Each card previews the actual TV background art (480px thumbs on GCS);
// `accent` drives the selected-card ring; `swatches` feed the ThemeBadge pill.
// Env-aware: prod build → popuppush-prod-media, dev → popuppush-dev-media (REACT_APP_MEDIA_BASE).
const THEME_ASSET_BASE = `${process.env.REACT_APP_MEDIA_BASE || 'https://storage.googleapis.com/popuppush-dev-media'}/system/birthday-themes/`;
// video: looping TV background (2026-06-06) — the SELECTED card plays it
// muted as a live preview; unselected cards keep the cheap static thumb.
const THEME_CARDS = [
  { id: 'party_neon',  emoji: '🎉', i18nKey: 'birthday.theme_party_neon',  thumb: `${THEME_ASSET_BASE}thumb_party_neon_480.jpg`,  video: `${THEME_ASSET_BASE}theme_party_neon.mp4`,  swatches: ['#14062e', '#ff2d9b'], accent: '#ff2d9b' },
  { id: 'royal_gold',  emoji: '👑', i18nKey: 'birthday.theme_royal_gold',  thumb: `${THEME_ASSET_BASE}thumb_royal_gold_480.jpg`,  video: `${THEME_ASSET_BASE}theme_royal_gold.mp4`,  swatches: ['#b8860b', '#f5d76e'], accent: '#d4af37' },
  { id: 'wow_pop',     emoji: '💥', i18nKey: 'birthday.theme_wow_pop',     thumb: `${THEME_ASSET_BASE}thumb_wow_pop_480.jpg`,     video: `${THEME_ASSET_BASE}theme_wow_pop.mp4`,     swatches: ['#b00f63', '#ff2d9b'], accent: '#ff1f8f' },
  { id: 'royal_dark',  emoji: '🎩', i18nKey: 'birthday.theme_royal_dark',  thumb: `${THEME_ASSET_BASE}thumb_royal_dark_480.jpg`,  video: `${THEME_ASSET_BASE}theme_royal_dark.mp4`,  swatches: ['#0a0a0a', '#d4af37'], accent: '#d4af37' },
  { id: 'sweet_candy', emoji: '🍭', i18nKey: 'birthday.theme_sweet_candy', thumb: `${THEME_ASSET_BASE}thumb_sweet_candy_480.jpg`, video: `${THEME_ASSET_BASE}theme_sweet_candy.mp4`, swatches: ['#fdeef5', '#e879ae'], accent: '#e879ae' },
];

// Sessions created before 2026-06-06 still carry old theme keys.
const LEGACY_THEME_MAP = { party: 'party_neon', elegant: 'royal_gold', kids: 'sweet_candy', cafe: 'royal_dark' };

/** Compact theme pill rendered next to the guest name on the active card.
 *  Picks colors from THEME_CARDS so palette stays single-source-of-truth. */
function ThemeBadge({ theme }) {
  const { t } = useTranslation();
  const card = THEME_CARDS.find((c) => c.id === (LEGACY_THEME_MAP[theme] || theme));
  if (!card) return null;
  return (
    <span
      className="pp-status"
      style={{
        background: `linear-gradient(135deg, ${card.swatches[0]}, ${card.swatches[1]})`,
        color: '#fff',
        textShadow: '0 1px 2px rgba(0,0,0,0.35)',
        fontWeight: 600,
        border: 'none',
      }}
      title={t(card.i18nKey)}
    >
      {card.emoji} {t(card.i18nKey)}
    </span>
  );
}

/** Pulls "song-name.mp3" out of "https://.../uploads/song-name.mp3?x=y" — keeps
 *  the audio-config card readable without storing a separate display name. */
function fileNameFromUrl(url) {
  if (!url) return null;
  try {
    const path = new URL(url).pathname;
    const last = decodeURIComponent(path.split('/').pop() || '');
    return last || null;
  } catch {
    const last = url.split('/').pop() || '';
    return last.split('?')[0] || null;
  }
}

export default function BirthdayMode() {
  const { t }     = useTranslation();
  const inHub = useInHub();
  const { showToast } = useCustomToast();

  const [open, setOpen] = useState(false);

  const { data: sessionsData, isLoading: sessionsLoading, error: sessionsError } = useGetBirthdaySessions();
  const sessions = useMemo(() => {
    const arr = sessionsData?.data ?? sessionsData ?? [];
    return Array.isArray(arr) ? arr : [];
  }, [sessionsData]);

  // Backend statuses (post 2026-06-02): PENDING → PREPARING → ACTIVE → COMPLETED.
  //   PREPARING = QR is collecting photos, player screens still show regular content.
  //   ACTIVE    = celebration running on screens.
  // Either state blocks starting a new session — show the right card for whichever
  // one is currently live.
  const currentSession = useMemo(
    () => sessions.find((s) => s.status === 'PREPARING' || s.status === 'ACTIVE') || null,
    [sessions]
  );
  // Legacy alias retained where the rest of the file only cares about "is something running".
  const activeSession = currentSession;

  if (sessionsError?.response?.status === 402) {
    return (
      <Layout>
        <FeatureLocked feature="birthday_mode" message={sessionsError.response.data?.message} />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container-fluid py-3" style={{ maxWidth: 980 }}>
        {/* Hero */}
        <div className="pp-card p-5 text-center" style={{
          background: 'linear-gradient(135deg, #fdf2f8 0%, #fce7f3 50%, #fff 100%)',
          borderColor: 'rgba(236, 72, 153, 0.25)',
        }}>
          <div style={{ fontSize: '3.5rem', lineHeight: 1 }}>🎂</div>
          {!inHub && <h2 className="pp-page-title justify-content-center mt-3 mb-1">{t('birthday.hero_title')}</h2>}
          <p className="text-muted mb-3" style={{ maxWidth: 520, margin: '0 auto' }}>
            {t('birthday.hero_subtitle')}
          </p>
          <Button
            icon="pi pi-gift"
            label={t('birthday.start')}
            onClick={() => setOpen(true)}
            disabled={!!activeSession}
            style={{
              background: 'linear-gradient(135deg, #ec4899, #be185d)',
              border: 'none',
              fontSize: '1rem',
              padding: '0.75rem 2rem',
              minHeight: 52,
            }}
          />
          {activeSession && (
            <p className="small text-muted mt-2 mb-0">{t('birthday.active_blocker')}</p>
          )}
        </div>

        {/* Current session card — different shape per status. */}
        {currentSession?.status === 'PREPARING' && (
          <PreparingSessionCard
            session={currentSession}
            onStarted={() => showToast('success', t('birthday.title'), t('birthday.started_toast') || 'Celebration started!')}
            onCancelled={() => showToast('success', t('birthday.title'), t('birthday.cancelled_toast') || 'Preparation cancelled.')}
          />
        )}
        {currentSession?.status === 'ACTIVE' && (
          <ActiveSessionCard
            session={currentSession}
            onEnded={() => showToast('success', t('birthday.title'), t('birthday.ended_toast'))}
          />
        )}

        {/* Audio templates configuration */}
        <AudioConfigCard />

        {/* Past celebrations */}
        <div className="pp-card mt-4">
          <div className="d-flex align-items-center justify-content-between p-3 border-bottom">
            <h5 className="mb-0">
              <i className="pi pi-history me-2" style={{ color: 'var(--pp-primary)' }} />
              {t('birthday.past_celebrations')}
            </h5>
            <span className="pp-status pp-status-neutral">{sessions.length}</span>
          </div>
          {sessionsLoading ? (
            <div className="p-3"><Skeleton height="2rem" className="mb-2" /><Skeleton height="2rem" className="mb-2" /><Skeleton height="2rem" /></div>
          ) : sessions.length === 0 ? (
            <div className="p-4 text-center text-muted small">{t('birthday.no_past')}</div>
          ) : (
            <div className="table-responsive">
              <table className="table mb-0">
                <thead>
                  <tr>
                    <th>{t('birthday.guest')}</th>
                    <th>{t('birthday.date')}</th>
                    <th>{t('birthday.duration')}</th>
                    <th>{t('birthday.screens')}</th>
                    <th>{t('birthday.status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.map((s) => {
                    const startedAt = s.starts_at || s.created_at;
                    const minutes   = s.duration_minutes ?? '—';
                    const isActive  = s.status === 'ACTIVE';
                    return (
                      <tr key={s.id}>
                        <td>🎂 <strong>{s.birthday_person_name || '—'}</strong></td>
                        <td><span className="small">{startedAt ? new Date(startedAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—'}</span></td>
                        <td>{minutes} {t('match_scheduler.min')}</td>
                        <td>{(s.device_ids?.length ?? s.devices?.length ?? 0)}</td>
                        <td>{isActive
                          ? <span className="pp-status pp-status-online">{t('birthday.active')}</span>
                          : <span className="pp-status pp-status-neutral">{t('birthday.ended')}</span>}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <StartBirthdayDialog open={open} onClose={() => setOpen(false)} />
    </Layout>
  );
}

/* ──────────────────────────────────────────────────────────────────────
 * Preparing session card — the centerpiece of the new pre-celebration flow.
 *
 * Layout reverses ActiveSessionCard: the QR is huge (this screen IS the
 * collection tool) and the photo grid is secondary. No countdown — the
 * timer doesn't start until the venue clicks "Start Celebration Now".
 *
 * Polls /sessions/{id}/photos every 10s so the count + grid update as
 * friends scan and upload from their phones.
 * ─────────────────────────────────────────────────────────────────── */

function PreparingSessionCard({ session, onStarted, onCancelled }) {
  const { t } = useTranslation();
  const startMut  = useStartPreparedBirthdaySession();
  const cancelMut = useEndBirthdaySession();

  const { data: photosData } = useGetBirthdaySessionPhotos(session.id, true);
  const photos     = photosData?.data ?? [];
  const photoCount = photosData?.count ?? photos.length;

  const onStart  = () => startMut.mutate(session.id,  { onSuccess: onStarted });
  const onCancel = () => cancelMut.mutate(session.id, { onSuccess: onCancelled });

  return (
    <div className="pp-card mt-4 p-4" style={{
      background: 'linear-gradient(180deg, #fff7ed 0%, #fff 100%)',
      borderColor: '#f59e0b',
      borderLeft: '4px solid #f59e0b',
    }}>
      <div className="d-flex align-items-start justify-content-between flex-wrap gap-3 mb-3">
        <div className="flex-grow-1">
          <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
            <h4 className="mb-0">🎂 {t('birthday.preparing_title') || 'Preparing'}: {session.birthday_person_name || '—'}</h4>
            {session.theme && <ThemeBadge theme={session.theme} />}
            <span className="pp-status" style={{
              background: 'rgba(245, 158, 11, 0.15)',
              color: '#92400e',
              border: '1px solid rgba(245, 158, 11, 0.3)',
              fontWeight: 600,
            }}>
              ⏳ {t('birthday.collecting') || 'Collecting photos'}
            </span>
          </div>
          {session.message && (
            <p className="mb-1" style={{ fontStyle: 'italic', color: '#92400e', fontSize: '1rem' }}>
              {session.message}
            </p>
          )}
          <p className="mb-0 small text-muted">
            <strong>{t('birthday.screens')}:</strong> {session.device_ids?.length ?? 0}
            {' · '}
            <strong>{t('birthday.duration')}:</strong> {session.duration_minutes ?? 5} {t('match_scheduler.min')}
          </p>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          <Button
            icon="pi pi-times"
            label={t('birthday.cancel_prep') || 'Cancel'}
            severity="secondary"
            outlined
            loading={cancelMut.isLoading}
            onClick={onCancel}
          />
          <Button
            icon="pi pi-play-circle"
            label={'🎉 ' + (t('birthday.start_now') || 'Start Celebration Now!')}
            loading={startMut.isLoading}
            onClick={onStart}
            style={{
              background: 'linear-gradient(135deg, #ec4899, #be185d)',
              border: 'none',
              minHeight: 48,
              fontSize: '1rem',
              fontWeight: 700,
            }}
          />
        </div>
      </div>

      {session.qr_url && (
        <div className="d-flex flex-wrap gap-4 align-items-start pt-3" style={{ borderTop: '1px dashed rgba(245,158,11,0.35)' }}>
          {/* BIG QR — this is the primary action of the prepare phase. */}
          <div className="d-flex flex-column align-items-center" style={{ minWidth: 280 }}>
            <div style={{
              background: '#fff',
              padding: 20,
              borderRadius: 16,
              boxShadow: '0 6px 24px rgba(0,0,0,0.12)',
              border: '2px solid rgba(245, 158, 11, 0.25)',
            }}>
              <QRCodeSVG value={session.qr_url} size={260} level="M" includeMargin={false} />
            </div>
            <p className="fw-bold mt-3 mb-1" style={{ color: '#92400e', fontSize: '1.05rem' }}>
              📱 {t('birthday.scan_to_upload_photos_of') || `Scan to upload photos of ${session.birthday_person_name}!`}
            </p>
            <p className="small text-muted mt-1 mb-0" style={{ wordBreak: 'break-all', maxWidth: 260, textAlign: 'center' }}>
              <a href={session.qr_url} target="_blank" rel="noopener noreferrer" style={{ color: '#b45309', textDecoration: 'none' }}>
                {t('birthday.qr_full_url') || 'Open link'}
              </a>
            </p>
          </div>

          {/* Collected grid — updates live every 10s. */}
          <div className="flex-grow-1">
            <div className="d-flex align-items-center gap-2 mb-2">
              <i className="pi pi-images" style={{ color: '#b45309' }} />
              <span className="fw-semibold">
                📸 {t('birthday.photos_collected', { n: photoCount }) || `Photos collected: ${photoCount}`}
              </span>
            </div>
            {photos.length === 0 ? (
              <div className="p-3 text-center small text-muted" style={{
                background: 'rgba(245, 158, 11, 0.05)',
                borderRadius: 8,
                border: '1px dashed rgba(245, 158, 11, 0.25)',
              }}>
                {t('birthday.waiting_first_photo') || 'Share the QR with friends to start collecting photos.'}
              </div>
            ) : (
              <div className="d-grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))' }}>
                {photos.map((p) => (
                  <a key={p.id} href={p.file_url} target="_blank" rel="noopener noreferrer"
                     style={{ position: 'relative', aspectRatio: '1', borderRadius: 8, overflow: 'hidden', border: '1px solid var(--pp-border)' }}>
                    <img src={p.file_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} loading="lazy" />
                  </a>
                ))}
              </div>
            )}
            <p className="small text-muted mt-3 mb-0">
              {t('birthday.start_hint') || 'When ready, click “Start Celebration Now!” — the screens will immediately show the pre-uploaded photos with the chosen theme.'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────
 * Active session card — guest, screens, countdown, QR, live photo grid,
 * End button. Polls /sessions/{id}/photos every 10s.
 * ─────────────────────────────────────────────────────────────────── */

function ActiveSessionCard({ session, onEnded }) {
  const { t } = useTranslation();
  const endMut = useEndBirthdaySession();

  // Countdown: seeded from the SERVER's `remaining_seconds`, never from
  // parsing an absolute timestamp.
  //
  // This used to do `new Date(session.starts_at) + duration_minutes`. That
  // was the birthday half of the platform timezone bug: the backend emitted a
  // naive string it had already shifted into a wrong zone, the browser then
  // re-parsed it as browser-local, and the resulting "end time" landed hours
  // in the PAST for any venue outside Saudi/Jordan — so `remainingMs` was 0 on
  // the very first render and the auto-end effect below instantly killed a
  // celebration that had just started.
  //
  // A relative seed is immune to all of that: we pin (local clock, server
  // remaining) once per session and count down from our own monotonic delta,
  // so no timezone, clock skew, or wire format can make a live session look
  // expired. Same pattern the Android happy-hour banner uses.
  const totalMin = session.duration_minutes ?? 5;

  // Pin the seed per session id. `remaining_seconds` is null for non-ACTIVE
  // rows; undefined if an older backend hasn't been deployed yet.
  const seedRef = useRef(null);
  if (seedRef.current?.id !== session.id) {
    const secs = typeof session.remaining_seconds === 'number'
      ? session.remaining_seconds
      : null;
    seedRef.current = { id: session.id, at: Date.now(), secs };
  }
  const seed = seedRef.current;

  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // No server value (older backend / non-ACTIVE row) → show the full duration
  // and DON'T auto-end. Failing open is the safe direction: a stuck "Active"
  // card is a cosmetic annoyance, a wrongly-ended celebration is not.
  const hasSeed = seed.secs !== null;
  const elapsedMs = now - seed.at;
  const remainingMs = hasSeed
    ? Math.max(0, seed.secs * 1000 - elapsedMs)
    : totalMin * 60_000;
  const mm = Math.floor(remainingMs / 60_000);
  const ss = Math.floor((remainingMs % 60_000) / 1000);

  const onEnd = () => endMut.mutate(session.id, { onSuccess: onEnded });

  // Auto-end when the countdown hits 0. The Android overlay closes itself
  // on its own timer, and a `birthday:expire` console command catches the
  // dashboard side server-side (once a scheduler is wired). Until then,
  // this is the immediate UX feedback: as soon as the visible 00:00 lands
  // we flip the session to COMPLETED so the user doesn't have to click End.
  //
  // Guards:
  //   - only fire once per session (autoEndedRef pins the id we ended).
  //   - require a real server seed. Without one we never auto-end — that's
  //     what let a parse failure silently terminate a live celebration.
  //   - require the timer to have actually RUN (elapsedMs > 0), so a session
  //     that arrives already at 0 still gets a render before we act.
  //   - skip while a manual end mutation is in flight to avoid a double-call.
  const autoEndedRef = useRef(null);
  useEffect(() => {
    if (!hasSeed || remainingMs > 0 || elapsedMs <= 0) return;
    if (endMut.isLoading) return;
    if (autoEndedRef.current === session.id) return;
    autoEndedRef.current = session.id;
    endMut.mutate(session.id, { onSuccess: onEnded });
  }, [remainingMs, hasSeed, elapsedMs, session.id, endMut, onEnded]);

  const { data: photosData } = useGetBirthdaySessionPhotos(session.id, true);
  const photos = photosData?.data ?? [];
  const photoCount = photosData?.count ?? photos.length;

  const photosCopy = photoCount === 0
    ? t('birthday.photos_uploaded_zero')
    : photoCount === 1
      ? t('birthday.photos_uploaded_one')
      : t('birthday.photos_uploaded', { n: photoCount });

  return (
    <div className="pp-card mt-4 p-4" style={{
      background: 'linear-gradient(180deg, #fdf2f8 0%, #fff 100%)',
      borderColor: '#ec4899',
      borderLeft: '4px solid #ec4899',
    }}>
      <div className="d-flex align-items-start justify-content-between flex-wrap gap-3 mb-3">
        <div className="flex-grow-1">
          <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
            <h4 className="mb-0">🎂 {session.birthday_person_name || t('birthday.active_title')}</h4>
            {session.theme && <ThemeBadge theme={session.theme} />}
          </div>
          {session.message && (
            <p className="mb-1" style={{ fontStyle: 'italic', color: '#be185d', fontSize: '1rem' }}>
              {session.message}
            </p>
          )}
          <p className="mb-0 small text-muted">
            <strong>{t('birthday.screens')}:</strong> {session.device_ids?.length ?? 0}
          </p>
        </div>
        <div className="text-center">
          <div className="small text-muted">{t('birthday.time_remaining')}</div>
          <div className="fw-bold" style={{ fontSize: '1.8rem', color: '#be185d', fontVariantNumeric: 'tabular-nums' }}>
            {String(mm).padStart(2, '0')}:{String(ss).padStart(2, '0')}
          </div>
        </div>
        <Button
          icon="pi pi-stop-circle"
          label={t('birthday.end')}
          loading={endMut.isLoading}
          onClick={onEnd}
          severity="danger"
        />
      </div>

      {/* QR + photos */}
      {session.qr_url && (
        <div className="d-flex flex-wrap gap-4 align-items-start pt-3" style={{ borderTop: '1px dashed rgba(236,72,153,0.3)' }}>
          <div className="d-flex flex-column align-items-center" style={{ minWidth: 200 }}>
            <div style={{
              background: '#fff',
              padding: 12,
              borderRadius: 12,
              boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            }}>
              <QRCodeSVG value={session.qr_url} size={180} level="M" includeMargin={false} />
            </div>
            <p className="small fw-semibold mt-2 mb-0" style={{ color: '#be185d' }}>📱 {t('birthday.scan_to_upload')}</p>
            <p className="small text-muted mt-1 mb-0" style={{ wordBreak: 'break-all', maxWidth: 200, textAlign: 'center' }}>
              <a href={session.qr_url} target="_blank" rel="noopener noreferrer" style={{ color: '#be185d', textDecoration: 'none' }}>
                {t('birthday.qr_full_url')}
              </a>
            </p>
          </div>

          <div className="flex-grow-1">
            <div className="d-flex align-items-center gap-2 mb-2">
              <i className="pi pi-images" style={{ color: '#be185d' }} />
              <span className="fw-semibold">📸 {photosCopy}</span>
            </div>
            {photos.length === 0 ? (
              <div className="p-3 text-center small text-muted" style={{
                background: 'rgba(236,72,153,0.05)',
                borderRadius: 8,
                border: '1px dashed rgba(236,72,153,0.2)',
              }}>
                {t('birthday.photos_uploaded_zero')}
              </div>
            ) : (
              <div className="d-grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))' }}>
                {photos.map((p) => (
                  <a key={p.id} href={p.file_url} target="_blank" rel="noopener noreferrer"
                     style={{ position: 'relative', aspectRatio: '1', borderRadius: 8, overflow: 'hidden', border: '1px solid var(--pp-border)' }}>
                    <img src={p.file_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} loading="lazy" />
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────
 * Audio Templates Configuration card — 4 slots, each settable from the
 * media library or YouTube. Saves via PUT /audio-settings.
 * ─────────────────────────────────────────────────────────────────── */

function AudioConfigCard() {
  const { t } = useTranslation();
  const { data: audioData, isLoading } = useGetBirthdayAudioSettings();
  const updateMut = useUpdateBirthdayAudioSettings();

  // Local pending state — user can tweak multiple slots, hit "Save Templates".
  const [pending, setPending] = useState(null);
  useEffect(() => {
    if (audioData) setPending({
      template_arabic_url:  audioData.template_arabic_url  || '',
      template_english_url: audioData.template_english_url || '',
      template_calm_url:    audioData.template_calm_url    || '',
      template_party_url:   audioData.template_party_url   || '',
    });
  }, [audioData]);

  const [pickerOpenForSlot, setPickerOpenForSlot] = useState(null);

  const setSlot = (slot, url) =>
    setPending((p) => ({ ...p, [slot]: url || '' }));

  const isDirty = useMemo(() => {
    if (!audioData || !pending) return false;
    return TEMPLATE_OPTIONS.some(
      (tpl) => (audioData[tpl.urlKey] || '') !== (pending[tpl.urlKey] || '')
    );
  }, [audioData, pending]);

  const onSave = () => updateMut.mutate(pending);

  return (
    <div className="pp-card mt-4">
      <div className="d-flex align-items-center justify-content-between p-3 border-bottom flex-wrap gap-2">
        <div>
          <h5 className="mb-1">
            <i className="pi pi-volume-up me-2" style={{ color: 'var(--pp-primary)' }} />
            {t('birthday.configure_audio')}
          </h5>
          <p className="text-muted small mb-0">{t('birthday.audio_subtitle')}</p>
        </div>
        {isDirty && (
          <Button
            icon="pi pi-save"
            label={t('birthday.save_audio_templates')}
            onClick={onSave}
            loading={updateMut.isLoading}
            style={{ background: 'linear-gradient(135deg, #ec4899, #be185d)', border: 'none' }}
          />
        )}
      </div>

      <div className="p-3 d-grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
        {isLoading || !pending ? (
          <>
            <Skeleton height="6rem" /><Skeleton height="6rem" /><Skeleton height="6rem" /><Skeleton height="6rem" />
          </>
        ) : (
          TEMPLATE_OPTIONS.map((tpl) => {
            const url = pending[tpl.urlKey];
            const fileName = fileNameFromUrl(url);
            return (
              <div key={tpl.id} className="p-3 rounded" style={{
                background: url ? 'color-mix(in srgb, var(--pp-btn-honey) 10%, transparent)' : 'transparent',
                border: `1px solid ${url ? 'var(--pp-btn-honey)' : 'var(--pp-border)'}`,
              }}>
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <span className="fw-semibold">
                    {tpl.emoji} {t(tpl.i18nKey)}
                  </span>
                  {url && (
                    <button
                      type="button"
                      className="btn btn-link p-0 small"
                      onClick={() => setSlot(tpl.urlKey, '')}
                      title={t('birthday.clear')}
                      style={{ color: 'var(--pp-text-muted)', textDecoration: 'none' }}
                    >
                      <i className="pi pi-times" />
                    </button>
                  )}
                </div>

                {url ? (
                  <>
                    <p className="small text-muted mb-2 text-truncate" title={fileName || url}>
                      {fileName || url}
                    </p>
                    <audio src={url} controls preload="none" style={{ width: '100%', height: 36 }} />
                    <Button
                      label={t('birthday.change')}
                      icon="pi pi-replay"
                      onClick={() => setPickerOpenForSlot(tpl)}
                      severity="secondary"
                      outlined
                      size="small"
                      className="mt-2"
                    />
                  </>
                ) : (
                  <>
                    <p className="small text-muted mb-2">{t('birthday.not_set')}</p>
                    <Button
                      label={t('birthday.choose')}
                      icon="pi pi-plus"
                      onClick={() => setPickerOpenForSlot(tpl)}
                      size="small"
                    />
                  </>
                )}
              </div>
            );
          })
        )}
      </div>

      <AudioPickerDialog
        open={!!pickerOpenForSlot}
        templateLabel={pickerOpenForSlot ? `${pickerOpenForSlot.emoji} ${t(pickerOpenForSlot.i18nKey)}` : ''}
        onClose={() => setPickerOpenForSlot(null)}
        onPick={({ url }) => {
          if (pickerOpenForSlot) setSlot(pickerOpenForSlot.urlKey, url);
          setPickerOpenForSlot(null);
        }}
      />
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────
 * Audio Picker — list of AUDIO+YOUTUBE media + "Search YouTube" button.
 * Returns { url, name } via onPick.
 * ─────────────────────────────────────────────────────────────────── */

function AudioPickerDialog({ open, onClose, onPick, templateLabel }) {
  const { t } = useTranslation();
  const { data: libData, isLoading, refetch } = useGetAudioLibrary(open);
  const [ytOpen, setYtOpen] = useState(false);

  const items = useMemo(() => {
    const arr = libData?.data ?? [];
    return arr.filter((m) => !m.is_folder && (m.type === 'AUDIO' || m.type === 'YOUTUBE'));
  }, [libData]);

  return (
    <>
      <Dialog
        header={<span><i className="pi pi-volume-up me-2" />{t('birthday.audio_picker_title')} — <span style={{ color: 'var(--pp-primary)' }}>{templateLabel}</span></span>}
        visible={open}
        onHide={onClose}
        style={{ width: 'min(96vw, 720px)', maxHeight: '85vh' }}
        modal
      >
        <div className="d-flex justify-content-between align-items-center mb-3 gap-2 flex-wrap">
          <span className="small text-muted">
            {items.length > 0 ? `${items.length} item${items.length === 1 ? '' : 's'}` : ''}
          </span>
          <Button
            icon="pi pi-youtube"
            label={t('birthday.from_youtube')}
            onClick={() => setYtOpen(true)}
            severity="danger"
            outlined
            size="small"
            style={{ borderColor: '#FF0000', color: '#FF0000' }}
          />
        </div>

        {isLoading ? (
          <Skeleton height="12rem" />
        ) : items.length === 0 ? (
          <div className="text-center py-5">
            <i className="pi pi-folder-open" style={{ fontSize: '3rem', color: 'var(--pp-text-muted)' }} />
            <p className="text-muted mt-2">{t('birthday.no_audio_in_library')}</p>
            <p className="small text-muted">
              <i className="pi pi-arrow-up" /> {t('birthday.from_youtube')}
            </p>
          </div>
        ) : (
          <div className="d-flex flex-column gap-2" style={{ maxHeight: '55vh', overflowY: 'auto' }}>
            {items.map((m) => {
              const isYt = m.type === 'YOUTUBE';
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => onPick({ url: m.path, name: m.file_name })}
                  className="d-flex align-items-center gap-3 p-2 rounded text-start"
                  style={{ border: '1px solid var(--pp-border)', background: 'transparent', cursor: 'pointer' }}
                >
                  <div style={{
                    width: 48,
                    height: 48,
                    borderRadius: 8,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: isYt ? '#fee2e2' : 'var(--pp-primary-subtle)',
                    flexShrink: 0,
                  }}>
                    <i className={isYt ? 'pi pi-youtube' : 'pi pi-volume-up'}
                       style={{ fontSize: '1.5rem', color: isYt ? '#FF0000' : 'var(--pp-primary)' }} />
                  </div>
                  <div className="flex-grow-1 text-truncate">
                    <div className="fw-semibold text-truncate" title={m.file_name}>{m.file_name}</div>
                    <div className="small text-muted">{isYt ? 'YouTube' : 'Audio'}</div>
                  </div>
                  <i className="pi pi-arrow-right" style={{ color: 'var(--pp-btn-honey)' }} />
                </button>
              );
            })}
          </div>
        )}
      </Dialog>

      <YouTubeSearchDialog
        visible={ytOpen}
        onHide={() => { setYtOpen(false); refetch(); }}
        onPickedVideo={(video) => {
          onPick({ url: video.url, name: video.title });
        }}
      />
    </>
  );
}

/* ──────────────────────────────────────────────────────────────────────
 * Start dialog — guest name, 4 fixed template radios with ▶ preview,
 * "Search YouTube instead" one-off, screens, duration.
 * ─────────────────────────────────────────────────────────────────── */

function StartBirthdayDialog({ open, onClose }) {
  const { t } = useTranslation();
  const { showToast } = useCustomToast();

  const [guestName, setGuestName] = useState('');
  // Optional guest contact — captured into the Customers directory only.
  // Never required; never affects the celebration.
  const [guestPhone, setGuestPhone] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [template,  setTemplate]  = useState('');
  const [deviceIds, setDeviceIds] = useState([]);
  const [duration,  setDuration]  = useState(5);

  // Celebration message — pick a preset OR write custom text.
  const [messagePreset, setMessagePreset] = useState(MESSAGE_PRESETS[0].id);
  const [customMessage, setCustomMessage] = useState('');

  // Visual theme — one of kids/elegant/party/cafe. Defaults to 'party'.
  const [theme, setTheme] = useState('party_neon');

  // One-off YouTube override (overrides the saved template URL on this run).
  const [oneOff, setOneOff] = useState(null);   // { url, title } | null
  const [ytOpen, setYtOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setGuestName('');
      setGuestPhone('');
      setGuestEmail('');
      setTemplate('');
      setDeviceIds([]);
      setDuration(5);
      setMessagePreset(MESSAGE_PRESETS[0].id);
      setCustomMessage('');
      setTheme('party_neon');
      setOneOff(null);
    }
  }, [open]);

  const { data: audioData,   isLoading: loadingAudio }   = useGetBirthdayAudioSettings();
  const { data: devicesData, isLoading: loadingDevices } = useGetAllCompanyDevices(open);

  const audioUrls = audioData ?? {};
  const availableTemplates = useMemo(
    () => TEMPLATE_OPTIONS.map((tpl) => ({ ...tpl, url: audioUrls[tpl.urlKey] || null })),
    [audioData] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const anyAvailable = availableTemplates.some((tpl) => tpl.url);

  // Default-select the first available template once audio settings load.
  useEffect(() => {
    if (open && !template && !oneOff) {
      const first = availableTemplates.find((tpl) => tpl.url);
      if (first) setTemplate(first.id);
    }
  }, [open, availableTemplates, template, oneOff]);

  const devices = useMemo(() => {
    const arr = Array.isArray(devicesData?.data) ? devicesData.data
              : Array.isArray(devicesData) ? devicesData : [];
    return arr;
  }, [devicesData]);

  const allSelected = devices.length > 0 && deviceIds.length === devices.length;
  const toggleAll = (checked) => setDeviceIds(checked ? devices.map((d) => d.id) : []);
  const toggleOne = (id, checked) => setDeviceIds((prev) =>
    checked ? [...new Set([...prev, id])] : prev.filter((x) => x !== id)
  );

  // New 3-step flow: this dialog calls /prepare, not /create. The session is
  // collected via the QR until the venue clicks "Start Celebration Now" on
  // the PreparingSessionCard, which calls /sessions/{id}/start.
  const prepareMut = usePrepareBirthdaySession();

  // Submit requires a template enum value. When the user picks a one-off
  // YouTube track we still default the template to 'arabic' so the backend
  // validation passes — the player prefers `audio_url` over the template URL.
  const effectiveTemplate = oneOff ? (template || 'arabic') : template;
  const canSubmit =
    guestName.trim().length > 0 &&
    deviceIds.length > 0 &&
    !!effectiveTemplate;

  const submit = () => {
    if (!canSubmit) {
      showToast('error', t('birthday.title'), t('birthday.fill_required'));
      return;
    }
    const resolvedMessage = (() => {
      if (messagePreset === 'custom') return customMessage.trim() || null;
      const preset = MESSAGE_PRESETS.find((p) => p.id === messagePreset);
      return preset?.text || null;
    })();

    const payload = {
      birthday_person_name: guestName.trim(),
      template:             effectiveTemplate,
      device_ids:           deviceIds,
      duration_minutes:     duration,
      theme,
      ...(resolvedMessage ? { message: resolvedMessage } : {}),
      ...(oneOff?.url      ? { audio_url:  oneOff.url   } : {}),
      ...(oneOff?.title    ? { audio_name: oneOff.title } : {}),
      // Optional CRM contact — only sent when filled; backend capture is fail-open.
      // The phone goes RAW, exactly as typed: PhoneNormalizer is the one component
      // that knows this venue's country and how to collapse a redundant dial code,
      // and prefixing one here would bypass it (F-3). Do not "help" it.
      ...(guestPhone.trim() ? { birthday_person_phone: guestPhone.trim() } : {}),
      ...(guestEmail.trim() ? { birthday_person_email: guestEmail.trim() } : {}),
    };
    prepareMut.mutate(payload, {
      onSuccess: () => onClose?.(),
    });
  };

  // Backwards-compat shim — many places downstream still reference `startMut`.
  const startMut = prepareMut;

  return (
    <>
      <Dialog
        header={<span>🎂 {t('birthday.start_dialog_title')}</span>}
        visible={open}
        onHide={onClose}
        style={{ width: 'min(95vw, 560px)' }}
        modal
      >
        <div className="d-flex flex-column" style={{ gap: '1.1rem' }}>
          {/* Guest name */}
          <div className="pp-field">
            <label className="pp-field-label">
              <i className="pi pi-user" />
              {t('birthday.guest')}
              <span className="pp-field-required">*</span>
            </label>
            <InputText
              value={guestName}
              onChange={(e) => setGuestName(e.target.value)}
              placeholder={t('birthday.guest_ph')}
              autoFocus
            />
          </div>

          {/* Birthday-person contact — saved to the Customers directory.
              BOTH STAY OPTIONAL and neither may ever become required: the person
              at the counter is usually a friend arranging a surprise and
              genuinely does not know either value. A guest can add both
              themselves at the recap opt-in during the celebration.

              P6 Step 3 — the PHONE leads, because it is the identity key: it is
              what unifies this celebration with the guest's Spin & Win and
              loyalty rows, so their points, birthday and history end up on ONE
              customer record. Email is second because it is the delivery key —
              the only channel the recap can actually go out on. Each field says
              which of the two it is, rather than both being labelled
              "(if known)", which told the venue nothing about why to bother. */}
          <p className="text-muted mb-2" style={{ fontSize: '0.82rem' }}>
            {t('birthday.guest_contact_hint')}
          </p>
          <div className="pp-field">
            <label className="pp-field-label">
              <i className="pi pi-phone" />
              {t('birthday.guest_phone')}
            </label>
            <InputText
              value={guestPhone}
              onChange={(e) => setGuestPhone(e.target.value)}
              dir="ltr"
              inputMode="tel"
              placeholder={t('birthday.guest_phone_ph')}
            />
            <p className="pp-field-hint">{t('birthday.guest_phone_hint')}</p>
          </div>
          <div className="pp-field">
            <label className="pp-field-label">
              <i className="pi pi-envelope" />
              {t('birthday.guest_email')}
            </label>
            <InputText
              value={guestEmail}
              onChange={(e) => setGuestEmail(e.target.value)}
              dir="ltr"
              inputMode="email"
              placeholder={t('birthday.guest_email_ph')}
            />
            <p className="pp-field-hint">{t('birthday.guest_email_hint')}</p>
          </div>

          {/* Celebration message — preset radios + custom text input */}
          <div className="pp-field">
            <label className="pp-field-label">
              <i className="pi pi-comment" />
              {t('birthday.message_label')}
            </label>
            <div className="d-flex flex-column gap-2">
              {MESSAGE_PRESETS.map((m) => {
                const selected = messagePreset === m.id;
                const label = m.id === 'custom' ? t('birthday.message_custom') : m.text;
                return (
                  <label key={m.id} className="d-flex align-items-center gap-2 p-2 rounded"
                         style={{
                           background: selected ? 'color-mix(in srgb, var(--pp-btn-honey) 10%, transparent)' : 'transparent',
                           border: selected ? '1px solid var(--pp-btn-honey)' : '1px solid var(--pp-border)',
                           cursor: 'pointer',
                         }}>
                    <RadioButton inputId={`msg-${m.id}`} value={m.id}
                      checked={selected}
                      onChange={(e) => setMessagePreset(e.value)} />
                    <span className="flex-grow-1">{m.emoji} {label}</span>
                  </label>
                );
              })}
              {messagePreset === 'custom' && (
                <InputText
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                  placeholder={t('birthday.message_custom_ph')}
                  maxLength={200}
                />
              )}
            </div>
          </div>

          {/* Visual theme picker — 5 premium cards previewing the actual
              TV background art (GCS thumbnails) */}
          <div className="pp-field">
            <label className="pp-field-label">
              <i className="pi pi-palette" />
              {t('birthday.theme_label')}
            </label>
            <div className="d-grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
              {THEME_CARDS.map((card) => {
                const selected = theme === card.id;
                return (
                  <button
                    key={card.id} type="button"
                    onClick={() => setTheme(card.id)}
                    className="d-flex flex-column p-0 rounded overflow-hidden"
                    style={{
                      background: 'var(--pp-bg)',
                      border: `2px solid ${selected ? card.accent : 'var(--pp-border)'}`,
                      cursor: 'pointer',
                      transition: 'transform 0.1s',
                      transform: selected ? 'scale(1.03)' : 'scale(1)',
                      boxShadow: selected ? `0 4px 14px ${card.accent}77` : 'none',
                    }}
                  >
                    {selected ? (
                      /* Live looping preview of the actual TV video bg.
                         poster = static thumb so the card never flashes
                         empty while the mp4 buffers. */
                      <video
                        src={card.video}
                        poster={card.thumb}
                        autoPlay muted loop playsInline
                        style={{
                          width: '100%',
                          aspectRatio: '16 / 9',
                          objectFit: 'cover',
                          display: 'block',
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: '100%',
                          aspectRatio: '16 / 9',
                          backgroundImage: `url(${card.thumb})`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                        }}
                      />
                    )}
                    <span
                      className="small fw-semibold py-1 px-2 d-flex align-items-center justify-content-center gap-1 w-100"
                      style={{
                        color: selected ? '#fff' : 'var(--pp-text)',
                        background: selected ? card.accent : 'transparent',
                      }}
                    >
                      {card.emoji} {t(card.i18nKey)}
                      {selected && <i className="pi pi-check-circle ms-1" style={{ fontSize: '0.8rem' }} />}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Music templates */}
          <div className="pp-field">
            <label className="pp-field-label">
              <i className="pi pi-volume-up" />
              {t('birthday.music')}
            </label>
            {loadingAudio ? (
              <Skeleton height="6rem" />
            ) : (
              <div className="d-flex flex-column gap-2">
                {oneOff ? (
                  <div className="d-flex align-items-center gap-2 p-2 rounded"
                       style={{ background: '#fee2e2', border: '1px solid #FF0000' }}>
                    <i className="pi pi-youtube" style={{ color: '#FF0000' }} />
                    <span className="flex-grow-1 text-truncate" title={oneOff.title}>{oneOff.title}</span>
                    <Button icon="pi pi-times" rounded text size="small" onClick={() => setOneOff(null)}
                            severity="secondary" aria-label={t('birthday.clear')} />
                  </div>
                ) : (
                  <>
                    {availableTemplates.map((tpl) => {
                      const disabled = !tpl.url;
                      const selected = template === tpl.id && !oneOff;
                      return (
                        <label key={tpl.id} className="d-flex align-items-center gap-2 p-2 rounded"
                               style={{
                                 background: selected ? 'color-mix(in srgb, var(--pp-btn-honey) 10%, transparent)' : 'transparent',
                                 border: selected ? '1px solid var(--pp-btn-honey)' : '1px solid var(--pp-border)',
                                 cursor: disabled ? 'not-allowed' : 'pointer',
                                 opacity: disabled ? 0.5 : 1,
                               }}>
                          <RadioButton inputId={`tpl-${tpl.id}`} value={tpl.id}
                            checked={selected} disabled={disabled}
                            onChange={(e) => setTemplate(e.value)} />
                          <span className="flex-grow-1">{tpl.emoji} {t(tpl.i18nKey)}</span>
                          {tpl.url ? (
                            <audio src={tpl.url} controls preload="none" style={{ height: 28, width: 160 }}
                                   onClick={(e) => e.stopPropagation()} />
                          ) : (
                            <span className="small text-muted">{t('birthday.not_set')}</span>
                          )}
                        </label>
                      );
                    })}
                    {!anyAvailable && (
                      <p className="pp-field-hint mb-0">{t('birthday.no_templates')}</p>
                    )}
                  </>
                )}
                <Button
                  icon="pi pi-youtube"
                  label={t('birthday.search_youtube_oneoff')}
                  onClick={() => setYtOpen(true)}
                  outlined
                  severity="danger"
                  size="small"
                  style={{ borderColor: '#FF0000', color: '#FF0000', alignSelf: 'flex-start' }}
                />
                <p className="small text-muted mb-0" style={{ marginTop: '-4px' }}>
                  {t('birthday.search_youtube_oneoff_hint')}
                </p>
              </div>
            )}
          </div>

          {/* Screens */}
          <div className="pp-field">
            <label className="pp-field-label">
              <i className="pi pi-desktop" />
              {t('quick_show.which_screens')}
              <span className="pp-field-required">*</span>
            </label>
            {loadingDevices ? (
              <Skeleton height="6rem" />
            ) : devices.length === 0 ? (
              <p className="pp-field-error"><i className="pi pi-exclamation-circle" />{t('quick_show.no_screens')}</p>
            ) : (
              <div className="d-flex flex-column gap-2" style={{ maxHeight: '25vh', overflowY: 'auto' }}>
                {devices.map((d) => (
                  <label key={d.id} className="d-flex align-items-center gap-2 p-2 rounded"
                         style={{
                           background: deviceIds.includes(d.id) ? 'color-mix(in srgb, var(--pp-btn-honey) 10%, transparent)' : 'transparent',
                           border: deviceIds.includes(d.id) ? '1px solid var(--pp-btn-honey)' : '1px solid var(--pp-border)',
                           cursor: 'pointer',
                         }}>
                    <Checkbox checked={deviceIds.includes(d.id)} onChange={(e) => toggleOne(d.id, e.checked)} />
                    <span className="fw-semibold">{d.name || d.serial_name}</span>
                  </label>
                ))}
                <div style={{ borderTop: '1px solid var(--pp-border)', margin: '0.25rem 0' }} />
                <label className="d-flex align-items-center gap-2 p-2 rounded fw-semibold"
                       style={{ cursor: 'pointer', color: 'var(--pp-btn-honey)' }}>
                  <Checkbox checked={allSelected} onChange={(e) => toggleAll(e.checked)} />
                  {t('quick_show.all_screens')}
                </label>
              </div>
            )}
          </div>

          {/* Duration */}
          <div className="pp-field">
            <label className="pp-field-label">
              <i className="pi pi-clock" />
              {t('birthday.duration')}
            </label>
            <div className="d-flex gap-2 flex-wrap">
              {DURATION_PRESETS.map((m) => (
                <button
                  key={m} type="button"
                  className={`pp-chip ${duration === m ? 'pp-chip-active' : ''}`}
                  onClick={() => setDuration(m)}
                  style={{ minHeight: 36 }}
                >
                  {t('quick_show.minutes', { n: m })}
                </button>
              ))}
            </div>
          </div>

          <div className="pp-dialog-footer">
            <Button label={t('buttons.cancel')} severity="secondary" outlined onClick={onClose} />
            <Button
              icon="pi pi-qrcode"
              label={'📱 ' + (t('birthday.prepare') || 'Prepare & share QR')}
              loading={prepareMut.isLoading}
              disabled={!canSubmit}
              onClick={submit}
              style={{ background: 'linear-gradient(135deg, #ec4899, #be185d)', border: 'none' }}
            />
          </div>
        </div>
      </Dialog>

      <YouTubeSearchDialog
        visible={ytOpen}
        onHide={() => setYtOpen(false)}
        onPickedVideo={(video) => setOneOff({ url: video.url, title: video.title })}
      />
    </>
  );
}
